// src/utils/uiUtils.js

/**
 * Helper that preserves all existing UI state when making changes to specific properties
 * This ensures that any element's visibility state is maintained even if not explicitly
 * referenced in your layout controls
 */
export const preserveUIState = (newUIState) => {
  return (dispatch, getState) => {
    // Get the current UI state
    const currentState = getState().ui;
    
    // Convert keys if needed between different naming conventions
    const normalizedNewState = {};
    
    // First, copy all explicit new state values
    Object.entries(newUIState).forEach(([key, value]) => {
      // Handle both naming conventions
      if (typeof value === 'boolean') {
        normalizedNewState[key] = value;
      }
    });

    // Next, check for naming convention differences and add compatibility
    const namingMap = {
      // Map between LMS and SCORM naming conventions
      'showLeftSidebar': 'leftSidebar',
      'showRightSidebar': 'rightSidebar',
      'showBottomPanel': 'bottomPanel',
      'showCourseNav': 'courseNavVisible'
    };

    // Ensure both naming conventions are included
    Object.entries(namingMap).forEach(([lmsKey, scormKey]) => {
      // If either naming convention is in the new state, copy to both
      if (normalizedNewState[lmsKey] !== undefined) {
        normalizedNewState[scormKey] = normalizedNewState[lmsKey];
      } else if (normalizedNewState[scormKey] !== undefined) {
        normalizedNewState[lmsKey] = normalizedNewState[scormKey];
      }
    });
    
    // Always ensure course navigation is visible
    normalizedNewState.showCourseNav = true;
    normalizedNewState.courseNavVisible = true;
    
    // Create a merged state that preserves all current values
    // and only updates the specific properties that were changed
    const mergedState = {
      ...currentState,  // Start with the complete current state
      ...normalizedNewState // Apply the normalized changes
    };
      
      // Log what's happening for debugging
    console.log('Preserving UI state with compatibility:', {
      before: currentState,
      changes: normalizedNewState,
      after: mergedState
    });
    
    // Dispatch with the full merged state
    dispatch({
      type: 'ui/setSidebarsVisibility',
      payload: mergedState
    });
  };
};
  
  /**
   * Reset UI visibility flags to their default values
   * This preserves any custom properties that might exist in the state
   */
  export const resetUIVisibility = () => {
    return (dispatch, getState) => {
      const uiState = getState().ui;
      
      // Create a new state object with visibility flags set to true
      const resetState = { ...uiState };
      
      // Only reset properties that look like visibility flags
      Object.keys(resetState).forEach(key => {
        if (typeof resetState[key] === 'boolean' && 
            (key.startsWith('show') || key === 'courseNavVisible')) {
          resetState[key] = true;
        }
      });
      
      // Always ensure course navigation is visible
      resetState.showCourseNav = true;
      resetState.courseNavVisible = true;
      
      // Dispatch the reset state
      dispatch({
        type: 'ui/setSidebarsVisibility',
        payload: resetState
      });
    };
  };